import { Toast } from '@base-ui/react'
import type { ToastVariant } from './toast'

export interface ToastOptions {
  description: string
  link?: {
    href: string
    text: string
  }
  timeout?: number
}

export interface ToastItem {
  id: string
  type: ToastVariant
  description: string
  link?: {
    href: string
    text: string
  }
}

/**
 * Hook for displaying toast notifications with a clean API.
 *
 * @returns Object with methods to show toasts and access active toasts
 *
 * @example
 * // Basic usage - show notifications
 * const toast = useToast()
 *
 * toast.success({ description: 'Changes saved successfully' })
 * toast.error({ description: 'Failed to save changes' })
 * toast.warning({ description: 'This action cannot be undone' })
 * toast.info({ description: 'New version available' })
 *
 * @example
 * // With link
 * toast.info({
 *   description: 'Update available',
 *   link: { href: '/changelog', text: 'View changes' }
 * })
 *
 * @example
 * // Custom timeout
 * toast.success({
 *   description: 'Quick message',
 *   timeout: 1000 // Auto-dismiss after 1 second
 * })
 *
 * @example
 * // Persistent toast (no auto-dismiss)
 * toast.error({
 *   description: 'Critical error',
 *   timeout: 0 // Must be manually closed
 * })
 *
 * @example
 * // Advanced: Access active toasts for custom rendering
 * const toast = useToast()
 *
 * // toast.toasts is a reactive array of currently active toast objects
 * // Each toast has: { id, type, description, link?, timeout }
 * // Primarily used for rendering custom toast lists
 * return (
 *   <div>
 *     {toast.toasts.map(t => (
 *       <CustomToast key={t.id} toast={t} onClose={() => toast.close(t.id)} />
 *     ))}
 *   </div>
 * )
 */
export function useToast() {
  const toastManager = Toast.useToastManager()

  const showToast = (variant: ToastVariant, options: ToastOptions) => {
    const { timeout, ...restOptions } = options

    return toastManager.add({
      type: variant,
      ...restOptions,
      ...(timeout !== undefined && { timeout }),
    })
  }

  return {
    error: (options: ToastOptions) => showToast('error', options),
    success: (options: ToastOptions) => showToast('success', options),
    warning: (options: ToastOptions) => showToast('warning', options),
    info: (options: ToastOptions) => showToast('information', options),
    close: (id: string) => toastManager.close(id),
    toasts: toastManager.toasts,
  }
}
